import { useMemo, useState } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { SearchFilterBar } from "@/components/shared/SearchFilterBar";
import { SortableTableHead } from "@/components/shared/SortableTableHead";
import type { SortDirection } from "@/components/shared/SortableTableHead";
import type { BrandSignal, SignalConfidence } from "@/data/mockBrandSignalsData";
import { ConfidenceBars, CONFIDENCE_LABELS, CONFIDENCE_ORDER } from "./ConfidenceBars";
import { BrandSignalDetailDrawer } from "./BrandSignalDetailDrawer";
import { SignalTableRow } from "./SignalTableRow";
import {
  NOISE_CONFIDENCES,
  ACTIONABLE_CONFIDENCES,
  SIGNAL_FILTER_COLUMNS,
  type SortField,
} from "./signalTableConstants";

interface BrandSignalsTableProps {
  signals: BrandSignal[];
  isLoading?: boolean;
  onUpdateTags: (signalId: string, tags: string[]) => Promise<void>;
  onCreateTest?: (signalId: string, hypothesis: string) => void;
}

export function BrandSignalsTable({ signals, isLoading, onUpdateTags, onCreateTest }: BrandSignalsTableProps) {
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState<Record<string, string[]>>({
    status: [],
    confidence: [],
    source: [],
  });
  const [showNoise, setShowNoise] = useState(false);
  const [selectedSignal, setSelectedSignal] = useState<BrandSignal | null>(null);
  const [sortField, setSortField] = useState<SortField | null>("updated");
  const [sortDir, setSortDir] = useState<SortDirection>("desc");

  const handleSort = (f: SortField) => {
    if (sortField !== f) {
      setSortField(f);
      setSortDir("desc");
    } else if (sortDir === "desc") {
      setSortDir("asc");
    } else {
      setSortField(null);
      setSortDir("desc");
    }
  };

  const filteredAll = useMemo(() => {
    const q = search.trim().toLowerCase();
    return signals.filter((s) => {
      if (q) {
        const hay = `${s.hypothesis} ${s.category} ${s.tags.join(" ")}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (filters.status.length && !filters.status.includes(s.status)) return false;
      if (filters.confidence.length && !filters.confidence.includes(s.confidence)) return false;
      if (filters.source.length && !filters.source.includes(s.source)) return false;
      return true;
    });
  }, [search, filters, signals]);

  const visible = useMemo(() => {
    const pool =
      showNoise || filters.confidence.length > 0
        ? filteredAll
        : filteredAll.filter((s) => ACTIONABLE_CONFIDENCES.includes(s.confidence));

    if (!sortField) return pool;
    return [...pool].sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case "hypothesis":
          cmp = a.hypothesis.localeCompare(b.hypothesis);
          break;
        case "category":
          cmp = a.category.localeCompare(b.category);
          break;
        case "confidence":
          cmp = CONFIDENCE_ORDER.indexOf(a.confidence) - CONFIDENCE_ORDER.indexOf(b.confidence);
          break;
        case "status":
          cmp = a.status.localeCompare(b.status);
          break;
        case "tests":
          cmp = (a.supportingTests + a.refutingTests) - (b.supportingTests + b.refutingTests);
          break;
        case "source":
          cmp = a.source.localeCompare(b.source);
          break;
        case "updated":
          cmp = new Date(a.lastUpdated).getTime() - new Date(b.lastUpdated).getTime();
          break;
      }
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [filteredAll, showNoise, filters.confidence, sortField, sortDir]);

  const noiseCount = useMemo(
    () => filteredAll.filter((s) => NOISE_CONFIDENCES.includes(s.confidence)).length,
    [filteredAll],
  );

  const totalActionable = filteredAll.filter((s) => ACTIONABLE_CONFIDENCES.includes(s.confidence)).length;

  const confidenceTooltip = (
    <div className="space-y-1.5">
      <p>Evidence confidence level.</p>
      <ul className="space-y-1">
        {(["low", "medium", "high"] as SignalConfidence[]).map((c) => (
          <li key={c} className="flex items-center gap-2">
            <ConfidenceBars confidence={c} />
            <span className="capitalize">{CONFIDENCE_LABELS[c]}</span>
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <SearchFilterBar
          search={search}
          onSearchChange={setSearch}
          placeholder="Search signals…"
          filterColumns={SIGNAL_FILTER_COLUMNS}
          activeFilters={filters}
          onFiltersChange={setFilters}
        />
        {noiseCount > 0 && filters.confidence.length === 0 && (
          <Button
            variant={showNoise ? "secondary" : "outline"}
            size="sm"
            onClick={() => setShowNoise((v) => !v)}
            className="text-xs"
          >
            {showNoise ? "Hide" : "Show"} {noiseCount} low-confidence
            {noiseCount !== 1 ? " signals" : " signal"}
          </Button>
        )}
        <span className="ml-auto text-xs text-muted-foreground">
          {showNoise || filters.confidence.length > 0
            ? `${visible.length} signal${visible.length !== 1 ? "s" : ""}`
            : `${totalActionable} actionable signal${totalActionable !== 1 ? "s" : ""}`}
        </span>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <SortableTableHead
              field="hypothesis"
              activeField={sortField}
              direction={sortDir}
              onSort={handleSort}
              tooltip="Plain-language statement of what the evidence suggests works."
              className="py-2 text-xs min-w-[380px] w-[380px]"
            >
              Hypothesis
            </SortableTableHead>
            <SortableTableHead
              field="category"
              activeField={sortField}
              direction={sortDir}
              onSort={handleSort}
              tooltip="Category of the signal hypothesis."
              className="py-2 text-xs min-w-[140px]"
            >
              Category
            </SortableTableHead>
            <SortableTableHead
              field="status"
              activeField={sortField}
              direction={sortDir}
              onSort={handleSort}
              tooltip="Current validation status of this signal."
              className="py-2 text-xs"
            >
              Status
            </SortableTableHead>
            <SortableTableHead
              field="confidence"
              activeField={sortField}
              direction={sortDir}
              onSort={handleSort}
              tooltip={confidenceTooltip}
              tooltipClassName="max-w-xs text-xs"
              className="py-2 text-xs"
            >
              Confidence
            </SortableTableHead>
            <SortableTableHead
              field="tests"
              activeField={sortField}
              direction={sortDir}
              onSort={handleSort}
              tooltip="Number of A/B tests contributing evidence to this signal."
              className="py-2 text-xs text-center"
            >
              Tests
            </SortableTableHead>
            <SortableTableHead
              field="source"
              activeField={sortField}
              direction={sortDir}
              onSort={handleSort}
              tooltip="Source of the signal: Signal AI (auto-detected) or Manual (analyst-created)."
              className="py-2 text-xs text-center"
            >
              Src
            </SortableTableHead>
            <SortableTableHead
              field="updated"
              activeField={sortField}
              direction={sortDir}
              onSort={handleSort}
              tooltip="Most recent date this signal received new evidence."
              className="py-2 text-xs text-right"
            >
              Updated
            </SortableTableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={7} className="text-center text-muted-foreground py-2 text-xs">
                Loading signals…
              </TableCell>
            </TableRow>
          ) : visible.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="text-center text-muted-foreground py-2 text-xs">
                No signals match the current filters.
              </TableCell>
            </TableRow>
          ) : (
            visible.map((s) => (
              <SignalTableRow key={s.id} signal={s} onClick={setSelectedSignal} />
            ))
          )}
        </TableBody>
      </Table>

      <BrandSignalDetailDrawer
        signal={selectedSignal}
        open={!!selectedSignal}
        onOpenChange={(o) => !o && setSelectedSignal(null)}
        onUpdateTags={onUpdateTags}
        onCreateTest={onCreateTest}
      />
    </div>
  );
}
